import {
  Autocomplete, Box, Button, FormControl, Grid, IconButton, InputLabel, MenuItem,
  Select, Tabs, TextField, Typography, Dialog, DialogContent, DialogContentText, DialogTitle,
  Tooltip
} from '@mui/material';
import { DataGridPro, GridCellModes } from '@mui/x-data-grid-pro';
import ExcelJS from 'exceljs';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  facilityBudgetsUpload, getExcelTypes, orderGuideBulkSubmit, priceUpdate, siteItemUpload, siteItemUploadWithPSNumber, uploadUsers, vendorAccountsUpload, vendorDatesUpload
  , invoiceGLBulkUpload, invoiceVendorBulkUpload, facilityUpload, purchasingCategoryUpload, getExcelTypeData
} from '../../../../api/excel';
import { RENTAL_UPLOADED_IMAGES } from '../../../../reusable-components/datagrid/rentalColumns';
import { CATALOG_IMAGE_UPLOAD } from '../../../../reusable-components/datagrid/catalogColumns';
import { postBulkAddProductImage } from '../../../../api/catalog';
import { HOST_API_URL } from '../../../../config-global';
import { AwaitButton } from '../../../../reusable-components/await-button';
import FileDropWrapper from '../../../../reusable-components/DragAndDrop/FileDropWrapper';
import Iconify from '../../../../reusable-components/iconify';
import { useSnackbar } from '../../../../reusable-components/snackbar';
import ExcelsDropdown from './ExcelsDropdown';
import FileAttachment from '../../../../reusable-components/file-attachment/FileAttachment';
import axiosInstance from '../../../../utils/axios';
import CloseIconButton from '../../../../reusable-components/basic-buttons/CloseIconButton';

export default function ExcelView() {
  const { enqueueSnackbar } = useSnackbar();
  const [selectedOption, setSelectedOption] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [typeData, setTypeData] = useState([]);
  const [options, setOptions] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([1]);
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [values, setValues] = useState({});
  const [file, setFile] = useState(null);
  const [openFileModel, setOpenFileModel] = useState(false);
  const [fileData, setFileData] = useState([]);
  const [columnType, setColumnType] = useState("");
  const [siteVendors, setSiteVendors] = useState([]);
  const [siteRentalVendors, setSiteRentalVendors] = useState([]);
  const [loadingSiteVendors, setLoadingSiteVendors] = useState(false);
  const [originalDataBySheet, setOriginalDataBySheet] = useState({});
  const [editedCellKeys, setEditedCellKeys] = useState(new Set());
  const workbookRef = useRef(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [cellModesModel, setCellModesModel] = useState({});

  const updateValuesByKey = (key, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };

  const updateNestedValuesByKey = (key, nestedKey, value) => {
    setValues((prevValues) => ({
      ...prevValues,
      [key]: {
        ...prevValues[key],
        [nestedKey]: value,
      },
    }));
  };

  const getValuesByKey = (key) => values[key];

  const getNestedValuesByKey = (key, nestedKey) => values?.[key]?.[nestedKey];

  const getOptions = async () => {
    const response = await getExcelTypes();
    if (response.status === 200) {
      setOptions(response.data);
      setSelectedOption(response.data[0]);
    }
  };

  useEffect(() => {
    getOptions();
  }, []);

  useEffect(() => {
    setValues({});
  }, [selectedOption]);

  // Fetched here rather than in ExcelsDropdown: the dropdown lives in the grid toolbar, which
  // remounts whenever its deps change (e.g. toggling edit mode), and would re-fire this request.
  useEffect(() => {
    if (!selectedOption) return;
    getExcelTypeData(selectedOption.id).then((response) => {
      if (response.status === 200) {
        setTypeData(response.data);
      }
    });
  }, [selectedOption]);

  const handleSubmitFile = async () => {
    setIsLoading(true);
    try {
      const imageUploadType = values?.imageUploadType;
      const imageUploadSite = values?.imageUploadSite.value;
      const imageUploadVendor = values?.imageUploadVendor.value;
      const response = await postBulkAddProductImage({ siteId: imageUploadSite, isRental: imageUploadType !== "purchasing", vendorId: imageUploadVendor, zipFile: file  });
      if (response.status === 200) {
         setColumnType(imageUploadType)
         setFileData(response.data.items)
         if(response.data.message === "All images matched and inserted successfully.") {
          enqueueSnackbar(response.data.message, { variant: 'success' });
         }
         else{
          enqueueSnackbar(response.data.message, { variant: 'error' });
         }
      } 
      else{
        enqueueSnackbar('Unexpected response from server.', { variant: 'error' });
      }
    } catch (error) {
      enqueueSnackbar(`There was an error uploading images: ${error.message}`, { variant: 'error' });
    }
    setValues({});
    setFile(null);
    setIsLoading(false);
  };          

  const fetchSiteVendors = (siteId, includeRentals) => {
    setSiteVendors([]);
    setSiteRentalVendors([]);
    if (siteId) {
      setLoadingSiteVendors(true); axiosInstance.get(`/ExcelUpload/SiteVendors?includeRentals=${!!includeRentals}&siteId=${siteId}`)
        .then(res => {
          setSiteVendors(res.data.purchasingVendors ?? []);
          setSiteRentalVendors(res.data.rentalVendors ?? []);
        })
        .catch(() => enqueueSnackbar("Failed to load vendors", { variant: "error" }))
        .finally(() => setLoadingSiteVendors(false));
    }
  };      
      
  const buildSubmissionFile = async () => {
    if (editedCellKeys.size === 0 || !workbookRef.current) return file;
    const buffer = await workbookRef.current.xlsx.writeBuffer();
    return new File([buffer], file.name, {
      type: file.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
  };

  const handleSubmit = async (testRun) => {
    setIsLoading(true);
    try {
      const vendorId = typeData?.excelUploadType?.useVendor && values.vendor?.value;
      const siteId = typeData?.excelUploadType?.useSites && values.sites.value;
      const excelUploadInputs =
        values?.excelUploadInputs &&
        Object.keys(values?.excelUploadInputs)?.map((key) => ({
          excelInputTypeId: key,
          col: values.excelUploadInputs[key]?.value ? values.excelUploadInputs[key].value.charCodeAt(0) - 64 : 0,
        })).filter(x => x.col !== 0);
      const ignoreRows = selectedRows;
      const sheetName = selectedSheet;
      const submissionFile = await buildSubmissionFile();

      let response;
      switch (selectedOption.id) {
        case 2:
          response = await siteItemUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 3:
          response = await priceUpdate({ vendorId, siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 5:
          response = await uploadUsers({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 7:
          response = await facilityBudgetsUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 9:
          response = await vendorDatesUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 10:
          response = await vendorAccountsUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 11:
          response = await siteItemUploadWithPSNumber({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 13:
          response = await orderGuideBulkSubmit({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 14:
          response = await invoiceGLBulkUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 15:
          response = await invoiceVendorBulkUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 16:
          response = await facilityUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        case 17:
          response = await purchasingCategoryUpload({ siteId, excelUploadInputs, ignoreRows, sheetName, testRun, file: submissionFile });
          break;
        default:
          throw new Error('Invalid operation selected.');
      }

      if (response.status === 200) {
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement('a');
        link.href = url;
        link.setAttribute('download', `ExcelUpload_${selectedOption.type}.xlsx`);
        document.body.appendChild(link);
        link.click();
        link.parentNode.removeChild(link);
        enqueueSnackbar('Download started.', { variant: 'success' });
      } else {
        enqueueSnackbar('Unexpected response from server.', { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
      enqueueSnackbar(`Error: ${error.message}`, { variant: 'error' });
    }
    setIsLoading(false);
  };

  const handleFileUpload = async (file) => {
    setIsLoading(true);
    setFile(file);
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer(), {
      ignoreNodes: ['hyperlinks'],
    });
    workbookRef.current = workbook;
    setOriginalDataBySheet({});
    setEditedCellKeys(new Set());
    setIsEditMode(false);
    setCellModesModel({});
    const sheetNames = workbook.worksheets.map((sheet) => sheet.name);
    setSheets(sheetNames);
    setSelectedSheet(sheetNames[0]);
    loadSheetData(workbook, sheetNames[0]);
  };

  useEffect(() => {
    if (!excelData?.length) return;
    if (!typeData?.reqTypes?.length) return;
    const headerRow = excelData[0];
    if (!headerRow) return;
    Object.entries(headerRow).forEach(([columnLetter, headerText]) => {
      if (columnLetter === 'row') return;
      const normHeader = (headerText ?? '').toString().trim().toLowerCase();
      typeData.reqTypes.forEach((reqType) => {
        const ruleId = reqType.rules.find((rule) => (rule.label ?? '').trim().toLowerCase() === normHeader)?.id;
        if (ruleId) updateNestedValuesByKey('excelUploadInputs', ruleId, { value: columnLetter, label: columnLetter });
      });
    });
  }, [excelData, typeData]);

  const loadSheetData = async (workbook, sheetName) => {
    setIsLoading(true);
    const worksheet = workbook.getWorksheet(sheetName);
    const rawData = [];
    worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
      const rowData = { row: rowNumber };
      row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
        const cellHeader = String.fromCharCode(64 + colNumber);
        rowData[cellHeader] = typeof cell.value === 'object' && cell.value ? cell.text : cell.value;
      });
      rawData.push(rowData);
    });

    const hasEmptyRow = rawData.some(row =>
      Object.keys(row)
        .filter(key => key !== 'row') // Exclude the 'row' key
        .every(key => row[key] === undefined || row[key] === null || row[key] === '')
    );
    if (hasEmptyRow) {
      enqueueSnackbar('One or more rows are empty. Please check the data.', { variant: 'error' });
    }
    // Capture the pristine, as-uploaded values for this sheet once, so edits can be reverted later.
    setOriginalDataBySheet((prev) =>
      prev[sheetName] ? prev : { ...prev, [sheetName]: rawData.map((row) => ({ ...row })) }
    );
    setExcelData(rawData);
    setIsLoading(false);
  };

  const handleSheetChange = (event) => {
    setValues({});
    const newSheet = event.target.value;
    setCellModesModel({});
    setSelectedSheet(newSheet);
    // Reuse the already-loaded workbook (rather than re-reading the raw file) so any edits made
    // on other sheets stay applied when navigating between sheets.
    if (workbookRef.current) {
      loadSheetData(workbookRef.current, newSheet);
    }
  };

  const originalRowsByRowNumber = useMemo(() => {
    const map = {};
    (originalDataBySheet[selectedSheet] || []).forEach((row) => { map[row.row] = row; });
    return map;
  }, [originalDataBySheet, selectedSheet]);

  const isCellEdited = (rowNumber, field) => editedCellKeys.has(`${selectedSheet}::${rowNumber}::${field}`);

  const hasEditsOnCurrentSheet = useMemo(
    () => Array.from(editedCellKeys).some((key) => key.startsWith(`${selectedSheet}::`)),
    [editedCellKeys, selectedSheet]
  );

  // Writes an edited value into both the displayed grid data and the in-memory workbook
  // (so the regenerated file submitted to the backend reflects the edit).
  const applyCellEdit = (rowNumber, field, value) => {
    const worksheet = workbookRef.current?.getWorksheet(selectedSheet);
    if (!worksheet) return;
    const colNumber = field.charCodeAt(0) - 64;
    worksheet.getRow(rowNumber).getCell(colNumber).value = value;

    setExcelData((prev) => prev.map((row) => (row.row === rowNumber ? { ...row, [field]: value } : row)));

    setEditedCellKeys((prev) => {
      const next = new Set(prev);
      const key = `${selectedSheet}::${rowNumber}::${field}`;
      const originalValue = originalRowsByRowNumber[rowNumber]?.[field];
      if (value === originalValue) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  const processRowUpdate = (newRow, oldRow) => {
    Object.keys(newRow).forEach((field) => {
      if (field === 'row') return;
      if (newRow[field] !== oldRow[field]) {
        applyCellEdit(newRow.row, field, newRow[field]);
      }
    });
    return newRow;
  };

  const handleProcessRowUpdateError = (error) => {
    console.error(error);
    enqueueSnackbar('Failed to apply the edit.', { variant: 'error' });
  };

  const revertCell = (rowNumber, field) => {
    const originalValue = originalRowsByRowNumber[rowNumber]?.[field];
    applyCellEdit(rowNumber, field, originalValue);
  };

  const revertAllForSheet = () => {
    const originalRows = originalDataBySheet[selectedSheet];
    const worksheet = workbookRef.current?.getWorksheet(selectedSheet);
    if (!originalRows || !worksheet) return;
    originalRows.forEach((originalRow) => {
      Object.keys(originalRow).forEach((field) => {
        if (field === 'row') return;
        const colNumber = field.charCodeAt(0) - 64;
        worksheet.getRow(originalRow.row).getCell(colNumber).value = originalRow[field];
      });
    });
    setExcelData(originalRows.map((row) => ({ ...row })));
    setEditedCellKeys((prev) => {
      const next = new Set(prev);
      Array.from(next).forEach((key) => {
        if (key.startsWith(`${selectedSheet}::`)) next.delete(key);
      });
      return next;
    });
  };

  const isRowIgnored = (rowNumber) => selectedRows.includes(rowNumber);

  const isCellEditable = (params) => isEditMode && params.field !== 'row' && !isRowIgnored(params.row.row);

  // Switching a cell to View mode commits its pending value, same as pressing Enter.
  const setAllCellsToView = (model) =>
    Object.fromEntries(
      Object.entries(model).map(([id, fields]) => [
        id,
        Object.fromEntries(Object.keys(fields).map((field) => [field, { mode: GridCellModes.View }])),
      ])
    );

  const toggleEditMode = () => {
    if (isEditMode) {
      setCellModesModel((prev) => setAllCellsToView(prev));
    }
    setIsEditMode((prev) => !prev);
  };

  const handleCellClick = (params, event) => {
    if (!params.isEditable) return;
    // Ignore clicks inside portals (e.g. tooltips) that aren't part of the cell.
    if (event.target.nodeType === 1 && !event.currentTarget.contains(event.target)) return;
    setCellModesModel((prev) => {
      const next = setAllCellsToView(prev);
      next[params.id] = { ...next[params.id], [params.field]: { mode: GridCellModes.Edit } };
      return next;
    });
  };

  const headerRow = excelData?.find(x => Object.keys(x).length > 1);
  const columns = headerRow ? [
    { field: 'row', headerName: '', width: 50, cellClassName: 'cell-ignored' },
    ...Object.keys(headerRow)
      .filter((key) => key !== 'row')
      .map((key) => ({
        field: key,
        headerName: key,
        width: 150,
        editable: true,
        cellClassName: (params) => (isCellEdited(params.row.row, key) ? 'cell-edited' : ''),
        renderCell: (params) => (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
            <Box component="span" sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>{params.value}</Box>
            {isEditMode && isCellEdited(params.row.row, key) && (
              <Tooltip title="Revert to original value">
                <IconButton
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    revertCell(params.row.row, key);
                  }}
                >
                  <Iconify icon="material-symbols:undo" width={16} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ),
      })),
  ]
    : [];


  const handleRowSelectionChange = (event, value) => {
    setSelectedRows(value);
  };

  const getRowClassName = (params) => (selectedRows.includes(params.row.row) ? 'row-ignored' : '');

  const CustomLeftToolbar = useCallback(
    () => (
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-end" sx={{ my: 1 }}>
        <Box>
          <Tabs value={0}>
            <ExcelsDropdown
              selectedOption={selectedOption}
              setSelectedOption={setSelectedOption}
              options={options}
            />
          </Tabs>
        </Box>
        
        {
          selectedOption && selectedOption.id === 18 ? 
            <Button
              variant="contained"
              color="secondary"
              size="small"
              startIcon={<Iconify icon="material-symbols:upload" />}
              sx={{ ml: 2 }}
              onClick={() => {setOpenFileModel(true)}}
            >
              Upload Image Files
            </Button>
          :
          <>
            <FileDropWrapper onFileDropped={handleFileUpload} accept='.xlsx, .xls'  >
              <Button
                variant="contained"
                color="secondary"
                size="small"
                startIcon={<Iconify icon="material-symbols:upload" />}
                sx={{ ml: 2 }}
              >
                Upload Excel
              </Button>
            </FileDropWrapper>
            <Button
              variant="outlined"
              color="secondary"
              size="small"
              onClick={() => {
                window.open(`${HOST_API_URL}Api/ExcelUpload/ExcelUploadTemplate?typeId=${selectedOption.id}`, '_blank');
              }}
              startIcon={<Iconify icon="file-icons:microsoft-excel" />}
              sx={{ ml: 2 }}
            >
              Download Template
            </Button>
            {excelData?.length > 0 && (
              <Button
                variant={isEditMode ? 'contained' : 'outlined'}
                color="primary"
                size="small"
                startIcon={<Iconify icon={isEditMode ? 'material-symbols:check' : 'material-symbols:edit'} />}
                sx={{ ml: 2 }}
                onClick={toggleEditMode}
              >
                {isEditMode ? 'Save Changes' : 'Edit Cells'}
              </Button>
            )}
            {isEditMode && hasEditsOnCurrentSheet && (
              <Button
                variant="outlined"
                color="warning"
                size="small"
                startIcon={<Iconify icon="material-symbols:undo" />}
                sx={{ ml: 2 }}
                onClick={revertAllForSheet}
              >
                Revert All Edits
              </Button>
            )}
        </>
        }
      </Grid>
    ),
    [selectedOption, options, excelData, selectedSheet, sheets, hasEditsOnCurrentSheet, isEditMode]
  );
  // Dynamically generate column options from excelData
  let dynamicColumnOptions = [];
  if (excelData?.length > 0) {
    const firstRow = excelData[0];
    dynamicColumnOptions = Object.keys(firstRow)
      .filter((key) => key !== 'row')
      .map((key) => ({
        label: key,
        value: key,
      }));
  }

  const missingRequiredFields = useMemo(() => {
    let missing = false;
    typeData.reqTypes?.forEach((reqType) => {
      reqType.rules.forEach((rule) => {
        if (rule.required && !getNestedValuesByKey('excelUploadInputs', rule.id)) {
          missing = true;
        }
      });
    });
    return missing;
  }, [typeData, values]);
  const typeOptions = [{ label: 'Purchasing', value: 'purchasing' }, { label: 'Rentals', value: 'rentals' }];

  return (
    <>
      <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start" sx={{ mt: 1 }}>
        <Dialog
          open={openFileModel}
          onClose={(event, reason) => {
            if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
            setOpenFileModel(false);
          }}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            Upload Instructions
            <CloseIconButton onClick={() => setOpenFileModel(false)}/>
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx= {{color: "black"}}>
              Please upload a .zip file containing the image files. The .zip file should be structured as follows:
              <br />
              - Each image file should be named with the Vendor Item Id (e.g., 12345.jpg).
              <br />
              <Box sx={{ mt: 2 }}>
                <Autocomplete
                  size="small"
                  options={typeData?.sites || []}
                  value={getValuesByKey('imageUploadSite')}
                  onChange={(_, value) => {
                    updateValuesByKey('imageUploadVendor', null);
                    fetchSiteVendors(value?.value, true);
                    updateValuesByKey('imageUploadSite', value)
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Site *"
                      placeholder="Select Site"
                      autoComplete="off"
                    />
                  )}
                  sx={{ width: '100%', mb: 2 }}
                />
               <Autocomplete
                  size="small"
                  options={typeOptions}
                  getOptionLabel={(option) => option.label}
                  value={
                    typeOptions.find(
                      (option) => option.value === getValuesByKey('imageUploadType')
                    ) || null
                  }
                  onChange={(event, value) => {
                    updateValuesByKey('imageUploadType', value ? value.value : '');
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Type *"
                      placeholder="Select Type"
                      autoComplete="off"
                    />
                  )}
                  sx={{ width: '100%', mb: 2 }}
                />
                <Autocomplete
                  size="small"
                  options={values.imageUploadType === "purchasing" ? siteVendors : siteRentalVendors || []}
                  value={getValuesByKey('imageUploadVendor')}
                  onChange={(event, value) => updateValuesByKey('imageUploadVendor', value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Vendor *"
                      placeholder="Select Vendor"
                      autoComplete="off"
                    />
                  )}
                  sx={{ width: '100%', mb: 2 }}
                />
              </Box>
              After uploading, the system will process the .zip file and associate the images with the correct PS Numbers based on the folder names.
            </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <FileDropWrapper
              onFileDropped={(file) => {
                // Only accept .zip files
                if (file && file.name.endsWith('.zip')) {
                  setFile(file);
                  enqueueSnackbar('Zip file ready for upload. Press "Upload" to continue.', { variant: 'info' });
                } else {
                  enqueueSnackbar('Please upload a valid .zip file.', { variant: 'error' });
                }
              }}
              accept=".zip"
            >
              <Button
                variant="contained"
                color="primary"
                component="span"
                startIcon={<Iconify icon="material-symbols:upload" />}
                sx={{ mt: 2 }}
                fullWidth
              >
                Drop or Select Zip File
              </Button>
            </FileDropWrapper>
            {file && (
            <FileAttachment file={file} />
            )}
            <Tooltip title={!getValuesByKey('imageUploadSite') ? "Please select a site for image upload."
                                 : !getValuesByKey('imageUploadType') ? "Please select an image upload type."
                                 : !getValuesByKey('imageUploadVendor') ? "Please select a vendor for image upload."
                                 : file === null ? "Please select a zip file to upload."
                                 : ""}>
             <span>
              <AwaitButton
                variant="contained"
                color="primary"
                sx={{ mt: 2 }}
                fullWidth
                disabled={file === null || !getValuesByKey('imageUploadSite') || !getValuesByKey('imageUploadType') || !getValuesByKey('imageUploadVendor')}
                onClick={async () => {
                  setIsLoading(true);
                  try {
                    await handleSubmitFile();
                    setOpenFileModel(false);
                    setFile(null);
                  } catch (error) {
                    enqueueSnackbar('Failed to upload zip file.', { variant: 'error' });
                  }
                  setIsLoading(false);
                }}
                loading={isLoading}
              >
                Upload
              </AwaitButton>
              </span>
            </Tooltip>
          </Box>
          </DialogContent>  
        </Dialog>

          {excelData?.length > 0 && (
            <Grid container direction="column" justifyContent="flex-start" alignItems="flex-start" sx={{ mt: 2 }}>
              <FormControl sx={{ minWidth: 120 }}>
                <InputLabel id="sheet-select-label">Tab</InputLabel>
                <Select
                  labelId="sheet-select-label"
                  id="sheet-select"
                  value={selectedSheet}
                  label="Sheet"
                  onChange={handleSheetChange}
                  sx={{ mt: -1 }}
                >
                  {sheets.map((sheet) => (
                    <MenuItem key={sheet} value={sheet}>
                      {sheet}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Autocomplete
                size="small"
                multiple
                value={selectedRows}
                options={excelData?.map((data) => data.row)}
                getOptionLabel={(option) => option.toString()}
                onChange={handleRowSelectionChange}
                renderInput={(params) => <TextField {...params} label="Select Rows to Ignore" placeholder="Rows" autoComplete="off"/>}
                sx={{ width: '95%', mb: 2 }}
                disableCloseOnSelect
              />
              {typeData?.excelUploadType?.useSites && (
                <Autocomplete
                  size="small"
                  options={typeData.sites}
                  value={getValuesByKey('sites')}
                  onChange={(event, value) => {
                    if (typeData?.excelUploadType?.useVendor) {
                      updateValuesByKey('vendor', null);
                      fetchSiteVendors(value?.value);
                    }
                    updateValuesByKey('sites', value);
                  }}
                  renderInput={(params) => (
                    <TextField error={!getValuesByKey('sites')} {...params} label="Site *" placeholder="Site" autoComplete="off"/>
                  )}
                  sx={{ width: '95%', mb: 2 }}                  
                />
              )}
              {typeData?.excelUploadType?.useVendor && (
                <Autocomplete
                  size="small"
                  value={getValuesByKey('vendor')}
                  onChange={(event, value) => updateValuesByKey('vendor', value)}
                  options={siteVendors}
                  loading={loadingSiteVendors}
                  renderInput={(params) => (
                    <TextField error={!getValuesByKey('vendor')} {...params} label="Vendor *" placeholder="Vendor" autoComplete="off"/>
                  )}
                  sx={{ width: '95%', mb: 2 }}                  
                />
              )}       
              {typeData?.reqTypes?.length > 0 &&
                typeData.reqTypes.map((reqType, index) => (
                  <Box key={reqType.id}>
                    <Typography variant="subtitle1" sx={{ mt: 1, ml: 1 }}>
                      {reqType.label}
                    </Typography>
                    <Grid container direction="row" justifyContent="flex-start" alignItems="flex-start">
                      {reqType.rules.map((rule) => (
                        <Grid item xs={index === 0 ? 6 : 12} key={rule.id}>
                          <Autocomplete
                            key={rule.id}
                            size="small"
                            options={dynamicColumnOptions || []}
                            value={getNestedValuesByKey('excelUploadInputs', rule.id) || []}
                            onChange={(event, value) => updateNestedValuesByKey('excelUploadInputs', rule.id, value)}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label={`${rule.label} ${rule.required ? ' *' : ''}`}
                                error={rule.required && !getNestedValuesByKey('excelUploadInputs', rule.id)}
                                placeholder={rule.label}
                                inputProps={{
                                  ...params.inputProps,
                                  autoComplete: ['email', 'name', 'phone', 'address'].some(sub => rule.label.toLowerCase().includes(sub)) ? 'no-autocomplete' : 'off',
                                  id: `no-autofill-${rule.id}`
                                }}
                              />
                            )}
                            sx={{ width: '93%', mb: 2 }}                            
                          />
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                ))}

              {typeData?.excelUploadType?.hasTestCall && (
                <AwaitButton
                  fullWidth
                  variant="outlined"
                  size="small"
                  sx={{ mt: 1, width: '95%' }}
                  color="warning"
                  onClick={() => handleSubmit(true)}
                  disabled={
                    missingRequiredFields ||
                    (typeData?.excelUploadType?.useVendor && !getValuesByKey('vendor')) ||
                    (typeData?.excelUploadType?.useSites && !getValuesByKey('sites'))
                  }
                >
                  Test Excel
                </AwaitButton>
              )}
              <AwaitButton
                fullWidth
                variant="contained"
                size="small"
                sx={{ mt: 1, width: '95%' }}
                onClick={() => handleSubmit(false)}
                disabled={
                  missingRequiredFields ||
                  (typeData?.excelUploadType?.useVendor && !getValuesByKey('vendor')) ||
                  (typeData?.excelUploadType?.useSites && !getValuesByKey('sites'))
                }
              >
                Submit Excel
              </AwaitButton>
            </Grid>
          )}
       <Grid item xs={excelData?.length > 0 ? 10 : 12}>
          <Box sx={{ height: 'calc(100vh - 280px)', width: '100%' }}>
            {
              excelData?.length > 0 ? (
                 <DataGridPro
                    gridId="admin-excel"
                    rows={excelData || []}
                    columns={ columns}
                    getRowId={(row) => row.row}
                    components={{
                      Toolbar: CustomLeftToolbar,
                    }}
                    loading={isLoading}
                    //there is a bug where the loading spinner is showing  below the data grid when the data is empty i think
                    getRowClassName={getRowClassName}
                    isCellEditable={isCellEditable}
                    processRowUpdate={processRowUpdate}
                    cellModesModel={cellModesModel}
                    onCellModesModelChange={setCellModesModel}
                    onCellClick={handleCellClick}
                    onProcessRowUpdateError={handleProcessRowUpdateError}
                    sx={{
                      '& .row-ignored': {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                      },
                      '& .cell-ignored': {
                        backgroundColor: 'rgba(0, 0, 0, 0.12)',
                      },
                      '& .cell-edited': {
                        backgroundColor: 'rgba(255, 193, 7, 0.16)',
                      },
                    }}
                  />
              )
             : (
                <DataGridPro
                  gridId="admin-excel"
                  rows={fileData || []}
                  columns={ columnType === "rental" ? RENTAL_UPLOADED_IMAGES : CATALOG_IMAGE_UPLOAD}
                  getRowId={(row) => row.id}
                  components={{
                    Toolbar: CustomLeftToolbar,
                  }}
                  isLoading={isLoading}
                  //there is a bug where the loading spinner is showing  below the data grid when the data is empty i think
                  getRowClassName={getRowClassName}
                  sx={{
                    '& .row-ignored': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    },
                    '& .cell-ignored': {
                      backgroundColor: 'rgba(0, 0, 0, 0.12)',
                    },
                  }}
                />
                
              )
            }
           
          </Box>
        </Grid>
   </Grid>
     </>
  );
}
